import React, { useState } from "react";
import { BASE_URL } from "./constants";

const Icon = ({ name, className = "h-5 w-5" }) => {
  const paths = {
    arrow: <><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></>,
    eye: <><path d="M2.5 12s3.4-6 9.5-6 9.5 6 9.5 6-3.4 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></>,
    eyeOff: <><path d="m3 3 18 18" /><path d="M10.6 5.2A10.7 10.7 0 0 1 12 5c6.1 0 9.5 7 9.5 7a18 18 0 0 1-2.1 2.9" /><path d="M6.7 6.7C4 8.5 2.5 12 2.5 12s3.4 7 9.5 7c1.6 0 3-.4 4.2-1" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    lock: <><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
    shield: <><path d="M12 3 4.5 6v5.5c0 4.5 3.1 8.3 7.5 9.5 4.4-1.2 7.5-5 7.5-9.5V6L12 3Z" /><path d="m8.7 12 2.1 2.1 4.5-4.5" /></>,
    users: <><path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20" /><circle cx="10" cy="7" r="3" /><path d="M16 4.5a3 3 0 0 1 0 5.8M20 20v-1.5a3.5 3.5 0 0 0-2.5-3.4" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    spark: <><path d="m12 3-1.3 4.7L6 9l4.7 1.3L12 15l1.3-4.7L18 9l-4.7-1.3L12 3Z" /><path d="m19 15-.6 2.4L16 18l2.4.6L19 21l.6-2.4L22 18l-2.4-.6L19 15Z" /></>,
  };

  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
};

const RoleButton = ({ active, icon, title, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`group relative flex min-h-[76px] flex-1 items-center gap-3 rounded-2xl border p-3.5 text-left transition-all duration-200 ${
      active
        ? "border-[#F8D346] bg-amber-500/10 ring-2 ring-[#F8D346]/40 shadow-xs"
        : "border-stone-200 bg-white hover:border-[#F8D346]/40 hover:bg-stone-50/60"
    }`}
  >
    <span
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
        active
          ? "bg-[#F8D346] text-stone-950 font-bold shadow-md shadow-amber-400/20"
          : "bg-stone-100 text-stone-500 group-hover:bg-[#F8D346]/20 group-hover:text-stone-900"
      }`}
    >
      <Icon name={icon} className="h-[18px] w-[18px]" />
    </span>
    <span className="min-w-0">
      <span
        className={`block text-sm font-bold ${
          active ? "text-stone-950" : "text-stone-700"
        }`}
      >
        {title}
      </span>
      <span className="mt-0.5 block text-[11px] leading-4 text-stone-500">
        {description}
      </span>
    </span>
    {active && (
      <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[#F8D346] text-stone-950 font-bold">
        <Icon name="check" className="h-3 w-3" />
      </span>
    )}
  </button>
);

const CommonLogin = () => {
  const [formData, setFormData] = useState({ email: "", password: "", role: "staff" });
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const updateField = (field, value) => setFormData((current) => ({ ...current, [field]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      const endpoint = formData.role === "admin" ? "admin/login" : "staff/login";
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });
      const data = await response.json();

      if (data.status === "success") {
        if (formData.role === "admin") {
          localStorage.setItem("token", data.data.token);
          localStorage.setItem("admin", JSON.stringify(data.data.admin));
          window.location.href = "/admin/home";
        } else {
          localStorage.setItem("staffToken", data.data.token);
          localStorage.setItem("staff", JSON.stringify(data.data.staff));
          window.location.href = "/staff/dashboard";
        }
      } else {
        setError(data.message || "Login failed. Please verify your credentials.");
      }
    } catch (requestError) {
      console.error("Login error:", requestError);
      setError("We couldn't connect to the service. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "h-12 w-full rounded-xl border border-stone-200 bg-stone-50/70 pl-11 pr-4 text-sm text-stone-900 outline-none transition placeholder:text-stone-400 hover:border-stone-300 focus:border-[#F8D346] focus:bg-white focus:ring-4 focus:ring-[#F8D346]/20";

  return (
    <main className="min-h-screen bg-[#faf9f7] font-sans text-stone-900 selection:bg-[#F8D346]/40 selection:text-stone-950 lg:flex">
      {/* Left Showcase Banner */}
      <section className="relative hidden min-h-screen overflow-hidden bg-[#0c0a09] text-white lg:flex lg:w-[49%] xl:w-[52%]">
        <img
          src="/login_bg.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-screen"
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_16%,rgba(248,211,70,0.22),transparent_35%),radial-gradient(circle_at_82%_82%,rgba(217,119,6,0.14),transparent_40%),linear-gradient(150deg,#0a0a08_0%,#171512_50%,#0e0d0b_100%)]" />
        <div className="absolute -bottom-32 -left-28 h-[420px] w-[420px] rounded-full border border-[#F8D346]/10" />
        <div className="absolute -bottom-16 -left-12 h-[300px] w-[300px] rounded-full border border-[#F8D346]/10" />

        <div className="relative z-10 flex w-full flex-col justify-between p-8 xl:p-12">
          {/* Top Branding */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl overflow-hidden border border-[#F8D346] shadow-sm bg-[#F8D346] shrink-0 p-0.5">
                <img
                  src="/inb_logo.jpg"
                  alt="INB Logo"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
              <div>
                <p className="text-[15px] font-extrabold tracking-tight text-white">
                  I Need Buddy
                </p>
                <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#F8D346]">
                  Operations Suite
                </p>
              </div>
            </div>
            <span className="rounded-full border border-[#F8D346]/30 bg-[#F8D346]/10 px-3 py-1.5 text-[10px] font-bold tracking-[0.16em] text-[#F8D346] backdrop-blur-md">
              EST. 2024
            </span>
          </div>

          {/* Central Messaging */}
          <div className="max-w-[500px] pb-4">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#F8D346]/20 bg-stone-900/80 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-stone-200 backdrop-blur-md">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_#34d399]" />
              Everything in sync
            </div>
            <h1 className="max-w-[470px] text-4xl font-black leading-[1.04] tracking-[-0.04em] xl:text-6xl text-white">
              Run better.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F8D346] via-amber-300 to-[#F8D346]">
                Serve smarter.
              </span>
            </h1>
            <p className="mt-6 max-w-[410px] text-sm leading-6 text-stone-300/80 xl:text-base">
              The calm, connected workspace for managing people, bookings, and citywide service operations.
            </p>

            <div className="mt-9 grid max-w-[440px] grid-cols-3 gap-3 border-t border-stone-800 pt-5">
              <div>
                <p className="text-2xl font-black tracking-tight text-white">24/7</p>
                <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.12em] text-[#F8D346]">
                  Live support
                </p>
              </div>
              <div>
                <p className="text-2xl font-black tracking-tight text-white">99.9%</p>
                <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.12em] text-[#F8D346]">
                  Uptime
                </p>
              </div>
              <div>
                <p className="text-2xl font-black tracking-tight text-white">1 hub</p>
                <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.12em] text-[#F8D346]">
                  Full control
                </p>
              </div>
            </div>
          </div>

          {/* Footer Metrics */}
          <div className="flex items-center justify-between border-t border-stone-800/80 pt-5 text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400">
            <span>Safer cities</span>
            <span className="h-1 w-1 rounded-full bg-[#F8D346]" />
            <span>Stronger communities</span>
            <span className="h-1 w-1 rounded-full bg-[#F8D346]" />
            <span>Better together</span>
          </div>
        </div>
      </section>

      {/* Right Form Section */}
      <section className="relative flex min-h-screen flex-1 flex-col justify-center overflow-hidden px-5 py-8 sm:px-10 lg:px-12 xl:px-20">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#F8D346]/10 blur-3xl" />
        <div className="relative z-10 mx-auto w-full max-w-[470px]">
          {/* Mobile Brand / Support Bar */}
          <div className="mb-8 flex items-center justify-between lg:mb-10">
            <div className="flex items-center gap-2.5 lg:hidden">
              <div className="w-9 h-9 rounded-xl overflow-hidden border border-[#F8D346] bg-[#F8D346] shrink-0 p-0.5">
                <img
                  src="/inb_logo.jpg"
                  alt="INB"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
              <span className="text-sm font-extrabold text-stone-950">I Need Buddy</span>
            </div>
            <div className="ml-auto flex items-center gap-2 text-xs text-stone-500">
              <span>Need help?</span>
              <a
                href="mailto:support@ineedbuddy.com"
                className="font-bold text-stone-900 hover:text-amber-700 underline decoration-[#F8D346] decoration-2 underline-offset-2 transition"
              >
                Contact support
              </a>
            </div>
          </div>

          <div className="mb-8">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-300/80 bg-amber-50/80 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-amber-950">
              <Icon name="shield" className="h-3.5 w-3.5 text-amber-600" />
              <span>Secure workspace</span>
            </div>
            <h2 className="text-3xl font-black tracking-[-0.04em] text-stone-950 sm:text-4xl">
              Welcome back<span className="text-[#F8D346]">.</span>
            </h2>
            <p className="mt-2 max-w-sm text-sm leading-6 text-stone-500">
              Sign in to keep your operations moving forward.
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="mb-5 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              <span className="mt-0.5 font-bold">!</span>
              <p className="leading-5">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <fieldset>
              <legend className="mb-2.5 text-xs font-bold text-stone-700 uppercase tracking-wider">
                Choose your workspace
              </legend>
              <div className="flex flex-col gap-2.5 sm:flex-row">
                <RoleButton
                  active={formData.role === "admin"}
                  icon="shield"
                  title="Admin portal"
                  description="Manage the whole operation"
                  onClick={() => updateField("role", "admin")}
                />
                <RoleButton
                  active={formData.role === "staff"}
                  icon="users"
                  title="Staff support"
                  description="Handle day-to-day work"
                  onClick={() => updateField("role", "staff")}
                />
              </div>
            </fieldset>

            <div>
              <label htmlFor="email" className="mb-2 block text-xs font-bold text-stone-700 uppercase tracking-wider">
                Work email
              </label>
              <div className="relative">
                <Icon
                  name="mail"
                  className="absolute left-3.5 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-stone-400"
                />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  className={inputClass}
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label htmlFor="password" className="text-xs font-bold text-stone-700 uppercase tracking-wider">
                  Password
                </label>
                <a
                  href="mailto:admin@ineedbuddy.com?subject=Password%20Reset%20Request"
                  className="text-xs font-bold text-stone-900 hover:text-amber-700 hover:underline transition"
                >
                  Forgot password?
                </a>
              </div>
              <div className="relative">
                <Icon
                  name="lock"
                  className="absolute left-3.5 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-stone-400"
                />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={(event) => updateField("password", event.target.value)}
                  className={`${inputClass} pr-12 tracking-wider`}
                  placeholder="••••••••••"
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-stone-400 transition hover:text-stone-700"
                >
                  <Icon name={showPassword ? "eyeOff" : "eye"} className="h-[18px] w-[18px]" />
                </button>
              </div>
            </div>

            <label className="flex cursor-pointer items-center gap-2.5 text-xs font-medium text-stone-600 select-none">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
                className="h-4 w-4 rounded border-stone-300 accent-amber-500 focus:ring-[#F8D346]"
              />
              <span>Keep me signed in on this device</span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="group flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-stone-950 hover:bg-stone-900 px-5 py-3.5 text-sm font-bold text-white shadow-xl shadow-stone-950/20 border border-stone-800 hover:border-[#F8D346]/80 transition-all focus:outline-none focus:ring-4 focus:ring-[#F8D346]/20 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#F8D346] border-t-transparent" />
                  <span>Signing you in...</span>
                </>
              ) : (
                <>
                  <span className="text-white group-hover:text-[#F8D346] transition-colors">
                    Continue to workspace
                  </span>
                  <Icon
                    name="arrow"
                    className="h-4 w-4 text-[#F8D346] transition-transform group-hover:translate-x-1"
                  />
                </>
              )}
            </button>
          </form>

          <div className="mt-7 flex items-center justify-center gap-2 text-xs text-stone-400">
            <Icon name="shield" className="h-4 w-4 text-[#F8D346]" />
            <span>Protected with encrypted sign-in</span>
          </div>
        </div>
        <p className="relative z-10 mt-10 text-center text-[10px] font-medium uppercase tracking-[0.15em] text-stone-400">
          © {new Date().getFullYear()} I Need Buddy · All systems operational
        </p>
      </section>
    </main>
  );
};

export default CommonLogin;
